import React, { FC, useCallback, useEffect, useState } from 'react';
import {
    Container,
    Grid,
    Button,
    Paper
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { useParams } from 'react-router';

import SFIARPToolbar from '../toolbar';
import { API_URL } from '../../env';
import {
    IAssessmentTemplate,
    IAssessmentTemplateDetail,
    IAssessmentTemplateGroup
} from '../../types/types';
import { get, post } from '../../fetchers/fetchers';
import { useTask } from '../../stateManagement/context';

import AssessmentTemplateGroupAccordion from './assessment_template_group_accordion';
import AssessmentTemplateGroupDialog from './assessment_template_group_dialog';
import AssessmentTemplateDialog, { TemplateFormValues } from './assessment_template_dialog';
import AssessmentTemplateDetailDialog, { DetailFormValues } from './assessment_template_detail_dialog';
import { ADEQUACY_OPTIONS } from './adequacy_options';
import { PreparedGroupSection } from './types';

const useStyles = makeStyles({
    tableHeaderCell: {
        textAlign: 'center'
    },
    groupAccordion: {
        margin: '0 !important',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        '&:before': {
            display: 'none'
        },
        '&.Mui-expanded': {
            margin: '0 !important'
        }
    },
    templateAccordion: {
        margin: '0 !important',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        '&:before': {
            display: 'none'
        },
        '&.Mui-expanded': {
            margin: '0 !important'
        }
    }
});

interface IRouteParams {
    id: string;
}

const AssessmentTemplateSettings: FC = () => {
    const classes = useStyles();
    const params = useParams<IRouteParams>();
    const tasks = useTask();

    const [assessmentTemplateGroups, setAssessmentTemplateGroups] = useState<IAssessmentTemplateGroup[]>([]);
    const [assessmentTemplates, setAssessmentTemplates] = useState<IAssessmentTemplate[]>([]);
    const [assessmentTemplateDetails, setAssessmentTemplateDetails] = useState<IAssessmentTemplateDetail[]>([]);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [expandedTemplates, setExpandedTemplates] = useState<string[]>([]);

    const [groupDialogOpen, setGroupDialogOpen] = useState<boolean>(false);
    const [templateDialogOpen, setTemplateDialogOpen] = useState<boolean>(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);

    const [editingGroup, setEditingGroup] = useState<IAssessmentTemplateGroup | null>(null);
    const [editingTemplate, setEditingTemplate] = useState<IAssessmentTemplate | null>(null);
    const [editingDetail, setEditingDetail] = useState<IAssessmentTemplateDetail | null>(null);

    const [groupInitialName, setGroupInitialName] = useState<string>('');
    const [templateInitialValues, setTemplateInitialValues] = useState<TemplateFormValues>({ name: '', display_order: 0 });
    const [detailInitialValues, setDetailInitialValues] = useState<DetailFormValues>({ description: '', adequacy_id: '' });
    const [pendingTemplateGroupUuid, setPendingTemplateGroupUuid] = useState<number | string | undefined>(undefined);
    const [detailParentTemplate, setDetailParentTemplate] = useState<IAssessmentTemplate | null>(null);
    const [detailDisabledAdequacyIds, setDetailDisabledAdequacyIds] = useState<number[]>([]);

    const canEdit = true//tasks?.write_access !== false;

    const getAssessmentTemplateGroups = async () => {
        return await get<IAssessmentTemplateGroup[]>(`${API_URL}assessment_templates/groups/${params.id}/`);
    };
    const getAssessmentTemplates = async () => {
        return await get<IAssessmentTemplate[]>(`${API_URL}assessment_templates/templates/${params.id}/`);
    };
    const getAssessmentTemplateDetails = async () => {
        return await get<IAssessmentTemplateDetail[]>(`${API_URL}assessment_templates/details/${params.id}/`);
    };

    const refreshData = useCallback(() => {
        Promise.all([
            getAssessmentTemplateGroups(),
            getAssessmentTemplates(),
            getAssessmentTemplateDetails()
        ]).then(([groups, templates, details]) => {
            setAssessmentTemplateGroups(groups);
            setAssessmentTemplates(templates);
            setAssessmentTemplateDetails(details);
        });
    }, [params.id]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const handleGroupToggle = (panelId: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpandedGroups(prev => {
            if (isExpanded) {
                return prev.includes(panelId) ? prev : [...prev, panelId];
            }
            return prev.filter(id => id !== panelId);
        });
    };

    const handleTemplateToggle = (panelId: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpandedTemplates(prev => {
            if (isExpanded) {
                return prev.includes(panelId) ? prev : [...prev, panelId];
            }
            return prev.filter(id => id !== panelId);
        });
    };

    const handleOpenCreateGroup = () => {
        setEditingGroup(null);
        setGroupInitialName('');
        setGroupDialogOpen(true);
    };

    const handleOpenEditGroup = (group: IAssessmentTemplateGroup) => {
        setEditingGroup(group);
        setGroupInitialName(group.name);
        setGroupDialogOpen(true);
    };

    const handleOpenCreateTemplate = (group: IAssessmentTemplateGroup) => {
        const templatesForGroup = assessmentTemplates.filter(t => {
            const tt = t as any;
            return tt.assessment_type_id === group.uuid || tt.group?.uuid === group.uuid || tt.group === group.uuid;
        });

        setEditingTemplate(null);
        setPendingTemplateGroupUuid(group.uuid ?? group.name);
        setTemplateInitialValues({
            name: '',
            display_order: templatesForGroup.length + 1
        });
        setTemplateDialogOpen(true);
    };

    const handleOpenEditTemplate = (template: IAssessmentTemplate) => {
        setEditingTemplate(template);
        setPendingTemplateGroupUuid((template as any).assessment_type_id || (template as any).group_uuid || template.group?.uuid);
        setTemplateInitialValues({
            name: template.name,
            display_order: Number(template.display_order) || 0
        });
        setTemplateDialogOpen(true);
    };

    const computeDisabledAdequacyIds = (template: IAssessmentTemplate, excludeDetailUuid?: number) => {
        const ids = assessmentTemplateDetails
            .filter(detail => {
                const dd = detail as any;
                return dd.template_uuid === template.uuid ||
                    dd.assessment_template_uuid === template.uuid ||
                    dd.template?.uuid === template.uuid;
            })
            .filter(detail => detail.uuid !== excludeDetailUuid)
            .map(detail => detail.adequacy?.id)
            .filter((id): id is number => typeof id === 'number');

        return Array.from(new Set(ids));
    };

    const handleOpenCreateDetail = (template: IAssessmentTemplate) => {
        setEditingDetail(null);
        setDetailParentTemplate(template);
        setDetailInitialValues({ description: '', adequacy_id: '' });
        const disabledIds = computeDisabledAdequacyIds(template);
        if (disabledIds.length >= ADEQUACY_OPTIONS.length) {
            alert('All adequacy levels are already assigned for this template. Please edit an existing detail.');
            return;
        }
        setDetailDisabledAdequacyIds(disabledIds);
        setDetailDialogOpen(true);
    };

    const handleOpenEditDetail = (detail: IAssessmentTemplateDetail, template: IAssessmentTemplate) => {
        setEditingDetail(detail);
        setDetailParentTemplate(template);
        setDetailInitialValues({
            description: (detail as any).description || (detail as any).justification || '',
            adequacy_id: (detail as any).adequacy_id || detail.adequacy?.id || ''
        });
        setDetailDisabledAdequacyIds(computeDisabledAdequacyIds(template, detail.uuid));
        setDetailDialogOpen(true);
    };

    const closeGroupDialog = () => {
        setGroupDialogOpen(false);
        setEditingGroup(null);
        setGroupInitialName('');
    };

    const closeTemplateDialog = () => {
        setTemplateDialogOpen(false);
        setEditingTemplate(null);
        setTemplateInitialValues({ name: '', display_order: 0 });
        setPendingTemplateGroupUuid(undefined);
    };

    const closeDetailDialog = () => {
        setDetailDialogOpen(false);
        setEditingDetail(null);
        setDetailParentTemplate(null);
        setDetailInitialValues({ description: '', adequacy_id: '' });
        setDetailDisabledAdequacyIds([]);
    };

    const handleGroupSubmit = async (name: string) => {
        const payload = {
            name,
            uuid: editingGroup?.uuid ?? -1
        };

        await post(`${API_URL}assessment_templates/groups/${params.id}/`, payload);

        closeGroupDialog();
        refreshData();
    };

    const handleRemoveGroup = async (group: IAssessmentTemplateGroup) => {
        if (!group.uuid) {
            return;
        }

        const payload = {
            name: group.name,
            uuid: group.uuid,
            is_active: false
        };

        await post(`${API_URL}assessment_templates/groups/${params.id}/`, payload);
        refreshData();
    };

    const handleTemplateSubmit = async (values: TemplateFormValues) => {
        const groupUuid = pendingTemplateGroupUuid || (editingTemplate as any)?.assessment_type_id || (editingTemplate as any)?.group_uuid || editingTemplate?.group?.uuid;
        if (!groupUuid) {
            return;
        }

        const payload = {
            name: values.name,
            display_order: Number(values.display_order) || 0,
            assessment_template_group_id: groupUuid,
            // assessment_type_id: N,
            uuid: editingTemplate?.uuid ?? -1
        };

        await post(`${API_URL}assessment_templates/templates/${params.id}/`, payload);

        closeTemplateDialog();
        refreshData();
    };

    const handleRemoveTemplate = async (template: IAssessmentTemplate) => {
        if (!template.uuid) {
            return;
        }

        const groupUuid = (template as any).assessment_type_id || (template as any).group_uuid || template.group?.uuid;
        if (!groupUuid) {
            return;
        }

        const payload = {
            name: template.name,
            display_order: Number((template as any).display_order) || 0,
            assessment_template_group_id: groupUuid,
            uuid: template.uuid,
            is_active: false
        };

        await post(`${API_URL}assessment_templates/templates/${params.id}/`, payload);
        refreshData();
    };

    const handleDetailSubmit = async (values: DetailFormValues) => {
        if (!detailParentTemplate) {
            return;
        }

        const adequacyId = Number(values.adequacy_id);
        if (!adequacyId) {
            return;
        }

        if (detailDisabledAdequacyIds.includes(adequacyId)) {
            alert('Each adequacy level can only be used once per template. Please choose a different adequacy.');
            return;
        }

        const payload = {
            description: values.description,
            assessment_template_id: detailParentTemplate.uuid,
            adequacy_id: adequacyId,
            uuid: editingDetail?.uuid ?? -1
        };

        await post(`${API_URL}assessment_templates/details/${params.id}/`, payload);

        closeDetailDialog();
        refreshData();
    };

    const handleRemoveDetail = async (detail: IAssessmentTemplateDetail, template: IAssessmentTemplate) => {
        if (!detail.uuid || !template.uuid) {
            return;
        }

        const adequacyId = Number((detail as any).adequacy_id || detail.adequacy?.id);
        if (!adequacyId) {
            return;
        }

        const payload = {
            description: (detail as any).description || (detail as any).justification || '',
            assessment_template_id: template.uuid,
            adequacy_id: adequacyId,
            uuid: detail.uuid,
            is_active: false
        };

        await post(`${API_URL}assessment_templates/details/${params.id}/`, payload);
        refreshData();
    };

    const getAdequacyChipStyles = (level?: number) => {
        let backgroundColor = 'rgb(196,196,196)';
        let color = '#535353';

        if (level === 1) {
            backgroundColor = 'rgba(206,238,208,1)';
            color = 'rgb(48,104,33)';
        }
        if (level === 2) {
            backgroundColor = 'rgba(252,236,166,1)';
            color = 'rgb(152,96,34)';
        }
        if (level === 3) {
            backgroundColor = 'rgba(251,84,81,1)';
            color = 'rgb(104,4,0)';
        }

        return { backgroundColor, color };
    };

    const preparedGroups: PreparedGroupSection[] = assessmentTemplateGroups.map((group, index) => {
        const groupUuid = (group.uuid ?? group.name ?? index).toString();
        const panelId = `group-${groupUuid}`;
        const templatesForGroup = assessmentTemplates.filter(t => {
            const tt = t as any;
            return tt.assessment_type_id === group.uuid || tt.group?.uuid === group.uuid || tt.group === group.uuid;
        });

        const templates = templatesForGroup.map(template => {
            const templatePanelId = `${groupUuid}-${template.uuid ?? template.name}`;
            const detailsForTemplate = assessmentTemplateDetails.filter(detail => {
                const dd = detail as any;
                return dd.template_uuid === template.uuid ||
                    dd.assessment_template_uuid === template.uuid ||
                    dd.template?.uuid === template.uuid;
            });

            return {
                template,
                panelId: templatePanelId,
                details: detailsForTemplate,
                expanded: expandedTemplates.includes(templatePanelId),
                onToggle: handleTemplateToggle(templatePanelId)
            };
        });

        return {
            group,
            panelId,
            templates,
            templateCount: templatesForGroup.length,
            expanded: expandedGroups.includes(panelId),
            onToggle: handleGroupToggle(panelId)
        };
    });

    return (
        <>
            <SFIARPToolbar title={'Assessment Template Settings'} />
            <Container>
                <Grid container spacing={3} xl={12} direction="row" style={{ padding: '20px', marginTop: '40px' }}>
                    <Grid item xs={7} sm={9} md={9} lg={10} container spacing={3}>
                        <Paper
                            variant="outlined"
                            style={{
                                padding: 16,
                                width: '100%',
                                backgroundColor: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 16
                            }}
                        >
                            {canEdit && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenCreateGroup}
                                    >
                                        Add group
                                    </Button>
                                </div>
                            )}
                            <div style={{ width: '100%' }}>
                                {preparedGroups.map(section => (
                                    <AssessmentTemplateGroupAccordion
                                        key={section.panelId}
                                        section={section}
                                        canEdit={canEdit}
                                        onCreateTemplate={handleOpenCreateTemplate}
                                        onEditGroup={handleOpenEditGroup}
                                        onCreateDetail={handleOpenCreateDetail}
                                        onEditTemplate={handleOpenEditTemplate}
                                        onEditDetail={handleOpenEditDetail}
                                        onRemoveGroup={handleRemoveGroup}
                                        onRemoveTemplate={handleRemoveTemplate}
                                        onRemoveDetail={handleRemoveDetail}
                                        getAdequacyChipStyles={getAdequacyChipStyles}
                                        classes={{
                                            groupAccordion: classes.groupAccordion,
                                            templateAccordion: classes.templateAccordion,
                                            tableHeaderCell: classes.tableHeaderCell
                                        }}
                                    />
                                ))}
                            </div>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            <AssessmentTemplateGroupDialog
                open={groupDialogOpen}
                initialName={groupInitialName}
                isEditing={Boolean(editingGroup)}
                onSubmit={handleGroupSubmit}
                onClose={closeGroupDialog}
            />

            <AssessmentTemplateDialog
                open={templateDialogOpen}
                initialValues={templateInitialValues}
                isEditing={Boolean(editingTemplate)}
                onSubmit={handleTemplateSubmit}
                onClose={closeTemplateDialog}
            />

            <AssessmentTemplateDetailDialog
                open={detailDialogOpen}
                initialValues={detailInitialValues}
                isEditing={Boolean(editingDetail)}
                onSubmit={handleDetailSubmit}
                onClose={closeDetailDialog}
                disabledAdequacyIds={detailDisabledAdequacyIds}
            />
        </>
    );
};

export default AssessmentTemplateSettings;
